/**
 * Move-group modal — the single surface for manually editing an account's
 * lifecycle (mirrors the synrgyinfinity MoveLifecycleModal).
 *
 * "Move stage" in the band opens this modal directly: the admin picks a
 * destination stage, satisfies the RFC gate if the move crosses it, then
 * confirms. The RFC gate is "provided," not "complete": moving to/past the
 * canonical RFC-approved milestone requires an RFC on file — cleared by
 * uploading ANY PDF (a dumb file-drop, no parsing; the file lands in the
 * account's Documents tab as an RFC attachment) or by filling out the digital
 * form. The server enforces the same rule (accountRfcProvided), so this UI is
 * a convenience, not the gate itself.
 *
 * Selecting "MSA Sent" hands off to the Send-MSA-Package (DocuSign) flow via
 * `onConfirm` — the send is what advances the account, never a raw stage set.
 *
 * Transition history lives here (read-only context for the admin making the
 * move), fetched lazily while the modal is open.
 */

import React from 'react';
import {
  AlertCircle,
  ArrowRight,
  Check,
  ChevronRight,
  Circle,
  CircleDot,
  Lock,
  PenLine,
  ShieldCheck,
  UploadCloud,
} from 'lucide-react';
import type {AccountsSubStage} from '@api/generated/client';
import {cn} from '@common-web/lib/utils';
import {trpc} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {
  DEFAULT_ACCOUNTS_VIEW_CONFIG,
  crossesRfcGate,
  forwardJumpSubStages,
  isForwardSubStageMove,
} from '@common/accountsView';
import {SUB_STAGE_LABEL} from '@common-web/components/account/phaseLabels';
import {clearRadixPointerEventsLock} from './LifecycleBand';
import {
  StageResolutionFields,
  buildStageResolutions,
  dateInputToDate,
  todayDateInputValue,
  type StageResolution,
  type StageResolutionDraft,
} from './StageResolutionFields';

// MSA Sent is reached by actually sending the MSA package (DocuSign), never by
// a raw stage set — selecting it hands off to the MSA send flow.
const MSA_SENT_SUB_STAGE: AccountsSubStage = 'MSA_SENT';

function fmtDateTime(value: Date | string): string {
  const d = value instanceof Date ? value : new Date(value);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * What a confirmed move carries besides the destination: the move's own
 * effective date ("when was the current stage completed?") and one resolution
 * per passed-over stage. Both are empty for backward moves.
 */
export interface MoveConfirmDetails {
  effectiveDate: Date | null;
  stageResolutions: StageResolution[];
}

interface MoveLifecycleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountsId: string;
  companyName: string;
  currentSubStage: AccountsSubStage;
  /** Sub-stages the caller may manually move to (from `_canTransition`). */
  editableSubStages: AccountsSubStage[];
  /** Whether the RFC gate is already satisfied (server-computed). */
  rfcProvided: boolean;
  /** Preselect a destination (e.g. the approve CTA opens straight to the gate). */
  initialTarget?: AccountsSubStage | null;
  isTransitioning?: boolean;
  /**
   * Confirm the move. The parent owns the mutation — and the MSA hand-off:
   * confirming with target MSA_SENT opens the Send-MSA-Package flow instead of
   * a raw stage set. `details` carries the forward move's effective date and
   * the per-passed-over-stage resolutions the server requires.
   */
  onConfirm: (target: AccountsSubStage, details: MoveConfirmDetails) => void;
  /** "Fill out digital form" — the structured alternative to the PDF drop. */
  onFillRfc: () => void;
  /** Whether this account's tenant has the "Welcome Call" implementation step
   *  (Synrgy/Quantum default-on). When false, the step is withheld from the
   *  destination picker — mirrors the band hiding it. */
  welcomeCallEnabled?: boolean;
}

export function MoveLifecycleModal({
  open,
  onOpenChange,
  accountsId,
  companyName,
  currentSubStage,
  editableSubStages,
  rfcProvided,
  initialTarget = null,
  isTransitioning = false,
  onConfirm,
  onFillRfc,
  welcomeCallEnabled = true,
}: MoveLifecycleModalProps): React.ReactElement {
  const [target, setTarget] = React.useState<AccountsSubStage | null>(initialTarget);
  const [view, setView] = React.useState<'pick' | 'upload'>('pick');
  // Per-passed-over-stage resolutions + the move's own effective date.
  const [resolutionDrafts, setResolutionDrafts] = React.useState<
    Record<string, StageResolutionDraft>
  >({});
  const [effectiveDate, setEffectiveDate] = React.useState(todayDateInputValue);

  // Reset per open so a stale destination/upload view never leaks between uses.
  React.useEffect(() => {
    if (open) {
      setTarget(initialTarget);
      setView('pick');
      setResolutionDrafts({});
      setEffectiveDate(todayDateInputValue());
    }
  }, [open, initialTarget]);

  const editable = React.useMemo(() => new Set(editableSubStages), [editableSubStages]);

  // Destination groups: one per configured stage, one row per editable
  // sub-stage. Same config fallback + canonical-anchor dedupe as the band's
  // old edit select — duplicates re-derive from the first match, so later
  // occurrences are unreachable here.
  // Same fallback the band applies: when the active config doesn't represent
  // the account's current sub-stage, render against the full default pipeline
  // so the picker never clamps the account to the wrong position.
  const activeConfig = useAccountsViewConfig();
  const stages = React.useMemo(() => {
    const currentRepresented = activeConfig.stages.some((s) =>
      s.subStages.some((sub) => sub.systemSubStage === currentSubStage),
    );
    return currentRepresented ? activeConfig.stages : DEFAULT_ACCOUNTS_VIEW_CONFIG.stages;
  }, [activeConfig, currentSubStage]);

  const groups = React.useMemo(() => {
    const seen = new Set<AccountsSubStage>();
    return stages
      .map((stage) => ({
        stage,
        items: stage.subStages.filter((sub) => {
          const sys = sub.systemSubStage as AccountsSubStage;
          // The Welcome Call step is per-tenant (Synrgy/Quantum) — never a
          // destination for accounts whose tenant has it off.
          if (sys === 'WELCOME_CALL' && !welcomeCallEnabled) return false;
          if (!editable.has(sys) || seen.has(sys)) return false;
          seen.add(sys);
          return true;
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [stages, editable, welcomeCallEnabled]);

  const labelFor = React.useCallback(
    (sub: AccountsSubStage): string => {
      for (const stage of stages) {
        const found = stage.subStages.find((s) => s.systemSubStage === sub);
        if (found) return found.label;
      }
      return SUB_STAGE_LABEL[sub] ?? sub;
    },
    [stages],
  );

  const gateCrossed = target ? crossesRfcGate(currentSubStage, target) : false;
  const needRfc = gateCrossed && !rfcProvided;
  const isMsaTarget = target === MSA_SENT_SUB_STAGE;

  // "MSA Sent" is reached by actually sending the package: the raw transition
  // the parent performs first only goes to MSA Pending, so that's the stage the
  // resolutions and effective date must cover.
  const resolutionTarget: AccountsSubStage | null = target
    ? isMsaTarget
      ? 'MSA_PENDING'
      : target
    : null;
  const isForward =
    !!resolutionTarget &&
    resolutionTarget !== currentSubStage &&
    isForwardSubStageMove(currentSubStage, resolutionTarget);

  // The stages this move passes over — each needs a date or a skip reason
  // before Confirm enables. The server enforces the same list
  // (requiredJumpResolutions), so this can't be bypassed.
  const passedOverSteps = React.useMemo(() => {
    if (!resolutionTarget || !isForward) return [];
    return forwardJumpSubStages(activeConfig, currentSubStage, resolutionTarget, {
      welcomeCallEnabled,
    }).map((sub) => ({
      subStage: sub as AccountsSubStage,
      label: labelFor(sub as AccountsSubStage),
    }));
  }, [resolutionTarget, isForward, activeConfig, currentSubStage, welcomeCallEnabled, labelFor]);

  const stageResolutions = React.useMemo(
    () => buildStageResolutions(passedOverSteps, resolutionDrafts),
    [passedOverSteps, resolutionDrafts],
  );

  const canConfirm =
    !!target &&
    target !== currentSubStage &&
    !needRfc &&
    !isTransitioning &&
    (!isForward || (!!effectiveDate && stageResolutions !== null));

  // Whether some configured stages are withheld from this caller (client
  // admins don't get the CBS-driven Implementation/Active moves).
  const showCbsNote = !editable.has('RFC_RECEIVED');

  const handleOpenChange = (next: boolean): void => {
    onOpenChange(next);
    if (!next) clearRadixPointerEventsLock();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        {view === 'upload' ? (
          <>
            <DialogHeader>
              <DialogTitle>Upload RFC — {companyName}</DialogTitle>
              <DialogDescription>
                Upload the completed RFC as a PDF. It&apos;s filed in the account&apos;s Documents —
                no field matching, and it doesn&apos;t need to be our form. Uploading it lets you
                move the account forward.
              </DialogDescription>
            </DialogHeader>
            <RfcPdfDrop accountsId={accountsId} onDone={() => setView('pick')} />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Move {companyName}</DialogTitle>
              <DialogDescription>
                Pick a destination stage. The move is recorded in the account&apos;s activity
                history.
              </DialogDescription>
            </DialogHeader>

            <div className='space-y-4'>
              {/* Current stage */}
              <div className='flex items-center gap-2 text-sm'>
                <span className='text-muted-foreground'>Currently</span>
                <span className='inline-flex items-center gap-1.5 font-semibold text-foreground'>
                  <CircleDot className='h-3.5 w-3.5 text-primary' />
                  {labelFor(currentSubStage)}
                </span>
              </div>

              {/* Destination list */}
              <div className='max-h-64 space-y-3 overflow-y-auto pr-1'>
                {groups.map(({stage, items}) => (
                  <div key={stage.key}>
                    <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                      {stage.label}
                    </p>
                    <div className='space-y-1'>
                      {items.map((sub) => {
                        const sys = sub.systemSubStage as AccountsSubStage;
                        const isCurrent = sys === currentSubStage;
                        const selected = target === sys && !isCurrent;
                        return (
                          <button
                            key={sub.key}
                            type='button'
                            disabled={isCurrent}
                            onClick={() => setTarget(sys)}
                            className={cn(
                              'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                              selected
                                ? 'border-primary bg-primary/5 font-medium text-foreground'
                                : isCurrent
                                  ? 'cursor-default border-transparent bg-muted text-muted-foreground'
                                  : 'border-border text-foreground hover:border-primary hover:bg-muted',
                            )}
                          >
                            {selected ? (
                              <Check className='h-4 w-4 shrink-0 text-primary' />
                            ) : (
                              <Circle className='h-4 w-4 shrink-0 text-muted-foreground/40' />
                            )}
                            <span className='flex-1'>{sub.label}</span>
                            {isCurrent ? (
                              <span className='text-xs font-medium text-muted-foreground'>
                                current
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {showCbsNote ? (
                  <p className='flex items-center gap-1.5 pt-0.5 text-xs text-muted-foreground'>
                    <Lock className='h-3 w-3' /> Implementation &amp; Active stages are managed
                    automatically by CBS.
                  </p>
                ) : null}
              </div>

              {/* RFC gate */}
              {gateCrossed ? (
                needRfc ? (
                  <div className='space-y-3 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/40'>
                    <div className='flex items-start gap-2'>
                      <AlertCircle className='mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400' />
                      <div>
                        <p className='text-sm font-semibold text-foreground'>
                          An RFC is required to move past this point
                        </p>
                        <p className='mt-0.5 text-xs text-muted-foreground'>
                          This account doesn&apos;t have an RFC on file yet. Upload a completed PDF,
                          or fill out the digital form.
                        </p>
                      </div>
                    </div>
                    <div className='grid grid-cols-2 gap-2'>
                      <button
                        type='button'
                        onClick={() => setView('upload')}
                        className='flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors hover:border-primary hover:bg-muted'
                      >
                        <UploadCloud className='h-4 w-4 shrink-0 text-primary' />
                        <span className='text-xs font-medium text-foreground'>Upload RFC PDF</span>
                      </button>
                      <button
                        type='button'
                        onClick={onFillRfc}
                        className='flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors hover:border-primary hover:bg-muted'
                      >
                        <PenLine className='h-4 w-4 shrink-0 text-primary' />
                        <span className='text-xs font-medium text-foreground'>
                          Fill out digital form
                        </span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className='flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'>
                    <ShieldCheck className='h-4 w-4 shrink-0' />
                    <span>RFC on file — this account can move past the RFC gate.</span>
                  </div>
                )
              ) : null}

              {/* Forward moves record their own effective date — no arbitrary
                  "now" stamps — plus a resolution for every passed-over step. */}
              {target && isForward && !needRfc ? (
                <div className='space-y-3 rounded-xl border border-border bg-muted/40 p-3'>
                  <div className='flex flex-wrap items-center justify-between gap-2'>
                    <label
                      htmlFor='move-effective-date'
                      className='text-xs font-medium text-foreground'
                    >
                      When was “{labelFor(currentSubStage)}” completed?
                    </label>
                    <Input
                      id='move-effective-date'
                      type='date'
                      value={effectiveDate}
                      max={todayDateInputValue()}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                      className='h-8 w-44 text-xs'
                    />
                  </div>
                  {passedOverSteps.length > 0 ? (
                    <StageResolutionFields
                      steps={passedOverSteps}
                      drafts={resolutionDrafts}
                      onDraftChange={(sub, draft) =>
                        setResolutionDrafts((prev) => ({...prev, [sub]: draft}))
                      }
                      disabled={isTransitioning}
                    />
                  ) : null}
                </div>
              ) : null}

              <TransitionHistory accountsId={accountsId} enabled={open} />

              {/* Confirm */}
              <div className='flex items-center justify-between gap-3 border-t border-border pt-3'>
                <div className='min-w-0 text-xs text-muted-foreground'>
                  {target && target !== currentSubStage ? (
                    <span className='inline-flex flex-wrap items-center gap-1.5'>
                      {labelFor(currentSubStage)}
                      <ArrowRight className='h-3 w-3 shrink-0' />
                      <span className='font-medium text-foreground'>{labelFor(target)}</span>
                    </span>
                  ) : null}
                </div>
                <div className='flex shrink-0 gap-2'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    size='sm'
                    disabled={!canConfirm}
                    onClick={() => {
                      if (!target) return;
                      onConfirm(target, {
                        effectiveDate:
                          isForward && effectiveDate ? dateInputToDate(effectiveDate) : null,
                        stageResolutions: isForward ? (stageResolutions ?? []) : [],
                      });
                    }}
                  >
                    {isMsaTarget
                      ? 'Send MSA Package'
                      : target
                        ? `Move to ${labelFor(target)}`
                        : 'Move'}
                    <ChevronRight className='ml-1 h-3.5 w-3.5' />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Plain RFC PDF drop — deliberately dumb: no reading, parsing, or field
 * matching. The file is uploaded to the account's Documents (category
 * RFC_ATTACHMENT), which satisfies the RFC gate server-side.
 */
function RfcPdfDrop({
  accountsId,
  onDone,
}: {
  accountsId: string;
  onDone: () => void;
}): React.ReactElement {
  const utils = trpc.useUtils();
  const [dragOver, setDragOver] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const generateUploadUrl = trpc.account.generateUploadUrl.useMutation();
  const confirmUpload = trpc.account.confirmUpload.useMutation();

  async function take(file: File): Promise<void> {
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please upload a PDF file.');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const contentType = file.type || 'application/pdf';
      const presigned = await generateUploadUrl.mutateAsync({
        accountsId,
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
        category: 'RFC_ATTACHMENT',
      });
      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {'Content-Type': contentType},
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed: ${putRes.status}`);
      }
      await confirmUpload.mutateAsync({
        accountsId,
        s3Key: presigned.s3Key,
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
        category: 'RFC_ATTACHMENT',
      });
      // The doc satisfies the gate — refresh so `rfcProvided` flips and the
      // Documents tab shows the file.
      utils.account.getById.invalidate({accountsId});
      utils.account.getDocuments.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className='space-y-4'>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f && !uploading) void take(f);
        }}
        className={cn(
          'block cursor-pointer rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors',
          dragOver
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary hover:bg-muted',
          uploading && 'pointer-events-none opacity-60',
        )}
      >
        <input
          ref={fileRef}
          type='file'
          accept='application/pdf,.pdf'
          className='hidden'
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void take(f);
          }}
        />
        <div className='flex flex-col items-center gap-2'>
          <UploadCloud className='h-6 w-6 text-primary' />
          <div className='text-sm font-medium text-foreground'>
            {uploading ? 'Uploading…' : 'Drop the RFC PDF here, or click to browse'}
          </div>
          <div className='text-xs text-muted-foreground'>PDF only · any PDF is accepted</div>
        </div>
      </label>
      {error ? (
        <div className='flex items-start gap-2 text-xs text-red-600'>
          <AlertCircle className='mt-0.5 h-3.5 w-3.5 shrink-0' /> {error}
        </div>
      ) : null}
      <div className='flex justify-end'>
        <Button type='button' variant='outline' size='sm' disabled={uploading} onClick={onDone}>
          Back
        </Button>
      </div>
    </div>
  );
}

// History actions that record a stage move — the modal's transition history is
// the activity feed narrowed to just these.
const STAGE_CHANGE_ACTIONS: ReadonlySet<string> = new Set(['SUB_STAGE_CHANGED', 'PHASE_CHANGED']);

/** Pull the recorded sub-stage out of a history row's JSON payload. */
function payloadSubStage(data: unknown): AccountsSubStage | null {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const value = (data as Record<string, unknown>).subStage;
    if (typeof value === 'string') return value as AccountsSubStage;
  }
  return null;
}

/**
 * Read-only list of the account's stage transitions (most recent first) —
 * moved here from the band's old Edit-lifecycle dialog. Fetches lazily: only
 * while the modal is open.
 */
function TransitionHistory({
  accountsId,
  enabled,
}: {
  accountsId: string;
  enabled: boolean;
}): React.ReactElement | null {
  const activity = trpc.account.getActivity.useQuery({accountsId}, {enabled});
  const transitions = React.useMemo(
    () => (activity.data ?? []).filter((row) => STAGE_CHANGE_ACTIONS.has(row.action)),
    [activity.data],
  );

  if (activity.isLoading) {
    return <p className='text-xs text-muted-foreground'>Loading transition history…</p>;
  }
  if (transitions.length === 0) return null;

  return (
    <details className='border-t border-border pt-3'>
      <summary className='cursor-pointer select-none text-xs text-muted-foreground'>
        Transition history ({transitions.length})
      </summary>
      <ul className='mt-2 max-h-32 space-y-1 overflow-y-auto pr-1'>
        {transitions.map((row) => {
          const from = payloadSubStage(row.oldData);
          const to = payloadSubStage(row.newData);
          return (
            <li key={row.id} className='flex items-center gap-1.5 text-xs text-muted-foreground'>
              <span className='text-foreground/80'>
                {from ? (SUB_STAGE_LABEL[from] ?? from) : 'created'}
              </span>
              <ArrowRight className='h-2.5 w-2.5 shrink-0' />
              <span className='font-medium text-foreground'>
                {to ? (SUB_STAGE_LABEL[to] ?? to) : '—'}
              </span>
              <span className='truncate'>
                · {row.actorUser?.name ?? 'System'} · {fmtDateTime(row.createdAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}
